import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js"
import paymentMethodPropType from "components/PropTypes/paymentMethodPropType"
import PayPalManager from "lib/PayPalManager"

const HIDDEN_STYLES = {
  display: "none",
}

const DISABLED_STYLES = {
  opacity: 0.2,
}

const PayPalButton = ({
  disabled = false,
  error = "",
  locale = "",
  preventDefault = false,
  paymentMethod = "",
  payPalManager = new PayPalManager({}),
}) => {
  const [{ isResolved }] = usePayPalScriptReducer()
  const [loadedPayPalManager, setLoadedPayPalManager] = useState(null)

  // Refs attach during React's commit phase, after render, so reading
  // `.current` synchronously during render can catch it before it's set.
  // Checking it in an effect instead means it runs after the ref has had a
  // chance to attach. Comparing by reference lets a new payPalManager (e.g.
  // once DonationFormManager gets a Stripe elements instance) invalidate any
  // stale "loaded" state from a previous manager, rather than assuming it's
  // still valid.
  useEffect(() => {
    const donationFormRef = payPalManager.donationForm
    if (donationFormRef?.current) {
      setLoadedPayPalManager(payPalManager)
    }
  }, [payPalManager])

  if (loadedPayPalManager !== payPalManager) {
    return null
  }

  const errorSpan = error ? <span className="text-red-700">{error}</span> : ""
  const visibleStyle = paymentMethod === "paypal" ? {} : HIDDEN_STYLES
  const disabledStyle = disabled ? DISABLED_STYLES : {}
  const activeClass = disabled ? "disabled" : "enabled"

  const { callbacks, onCancel, onClick } = payPalManager
  const style = { layout: "horizontal", size: "responsive", tagline: false, locale }

  // We pass className="opacity-100!" below to override PayPalButtons' default disabled styling
  // We want to have our button appear to be enabled when the form is invalid, but in that state
  // we don't want a button click to trigger PayPal's payment process
  const buttonsEl = isResolved ? (
    <PayPalButtons
      createOrder={callbacks?.createOrder}
      createSubscription={callbacks?.createSubscription}
      onApprove={callbacks?.onApprove}
      onError={callbacks?.onError}
      className="opacity-100!"
      disabled={disabled || preventDefault}
      commit={true}
      env={payPalManager.env}
      onCancel={onCancel}
      onClick={onClick}
      style={style}
      forceReRender={[payPalManager.frequency]}
    />
  ) : null

  return (
    <div
      id="paypal_container"
      data-testid="paypal_container"
      className={activeClass}
      style={{ ...visibleStyle, ...disabledStyle }}
    >
      <div id="paypal_button">{buttonsEl}</div>
      {errorSpan}
    </div>
  )
}

PayPalButton.propTypes = {
  classes: PropTypes.shape({ error: PropTypes.string }),
  disabled: PropTypes.bool,
  error: PropTypes.node,
  locale: PropTypes.string,
  preventDefault: PropTypes.bool,
  paymentMethod: paymentMethodPropType,
  payPalManager: PropTypes.instanceOf(PayPalManager),
}

export default PayPalButton
